import { ArpMedia, MintStatusModel, SamplePack } from '@arpeggi/shared'
import { useMemo, useReducer } from 'react'
import { Segment } from '../../../../../../features/segment'
import { useMintsAPI } from '../../../../../api/v2/MintsAPI'
import { LoadingStatus } from '../../../../../components/studio/activitybar/sample-packs/SamplePackDropdown'
import axios from '../../../../../lib/axios'
import {
  ArrayField,
  FileError,
  FileField,
  NumberField,
  resolveArrayError,
  resolveNumberError,
  resolveStringError,
  TextField
} from '../../../../../utils/forms'

export interface OptionalFields {
  color?: boolean
}

export enum Page {
  INSTRUCTIONS = 'instructions',
  UPLOAD_WAV = 'upload_wav',
  PUBLISH = 'publish',
  UPLOAD_PROJECT = 'upload_project',
  DETAILS = 'details',
  ATTRIBUTIONS = 'attributions',
  CONFIRMATION = 'confirmation'
}

const pages = Object.values(Page)

enum ActionType {
  SET_PAGE = 'set_page',
  RESET = 'reset',
  SET_WAV_FILE = 'set_wav_file',
  SET_WAV_FILE_ERRORS = 'set_wav_file_errors',
  CLEAR_WAV_FILE = 'clear_wav_file',
  SET_PROJECT_FILE = 'set_project_file',
  SET_PROJECT_FILE_ERRORS = 'set_project_file_errors',
  CLEAR_PROJECT_FILE = 'clear_project_file',
  UPDATE_MINT_DETAILS = 'update_mint_details',
  SET_DETECTED_SUB_COMPONENT_IDS = 'set_detected_sub_component_ids',
  SET_MINTING_STATUS = 'set_minting_status',
  MINT_SUCCESS = 'mint_sucess',
  ADD_SELECTED_SUB_COMPONENT = 'add_selected_sub_component',
  REMOVE_SELECTED_SUB_COMPONENT = 'remove_selected_sub_component'
}

type Action =
  | { type: ActionType.SET_PAGE; payload: { page: Page } }
  | { type: ActionType.RESET }
  | { type: ActionType.SET_WAV_FILE; payload: { file: File | undefined } }
  | { type: ActionType.SET_WAV_FILE_ERRORS; payload: { errors: FileError[] | undefined } }
  | { type: ActionType.CLEAR_WAV_FILE }
  | { type: ActionType.SET_PROJECT_FILE; payload: { file: File | undefined } }
  | { type: ActionType.SET_PROJECT_FILE_ERRORS; payload: { errors: FileError[] | undefined } }
  | { type: ActionType.CLEAR_PROJECT_FILE }
  | { type: ActionType.UPDATE_MINT_DETAILS; payload: { mintDetails: Partial<MintDetails> } }
  | { type: ActionType.SET_DETECTED_SUB_COMPONENT_IDS; payload: { subComponentIds: number[] } }
  | { type: ActionType.SET_MINTING_STATUS; payload: { status: LoadingStatus } }
  | { type: ActionType.MINT_SUCCESS; payload: { createdMintStatus: MintStatusModel } }
  | { type: ActionType.ADD_SELECTED_SUB_COMPONENT; payload: { id: number } }
  | { type: ActionType.REMOVE_SELECTED_SUB_COMPONENT; payload: { id: number } }

export interface MintDetails {
  title: TextField
  genres: ArrayField<string>
  key: NumberField
  bpm: NumberField
  color?: TextField
  customWalletAddress?: TextField
}

export interface State {
  page: Page
  wavFileField: FileField
  projectFileField: FileField
  mintDetails: MintDetails
  detectedSubComponentIds: number[]
  selectedSubComponentIds: number[]
  mintingStatus: LoadingStatus
  remixOfSamplePack?: SamplePack
  createdMintStatus?: MintStatusModel
  sounds?: ArpMedia[]
}

function reducer(state: State, action: Action) {
  switch (action.type) {
    case ActionType.SET_PAGE:
      return {
        ...state,
        page: action.payload.page
      }
    case ActionType.RESET:
      return {
        ...state,
        ...initialState
      }
    case ActionType.SET_WAV_FILE:
      return {
        ...state,
        wavFileField: { ...state.wavFileField, value: action.payload.file, errors: undefined }
      }
    case ActionType.SET_WAV_FILE_ERRORS:
      return {
        ...state,
        wavFileField: { ...state.wavFileField, errors: action.payload.errors, value: undefined }
      }
    case ActionType.CLEAR_WAV_FILE:
      return {
        ...state,
        wavFileField: { value: undefined, errors: undefined }
      }
    case ActionType.SET_PROJECT_FILE:
      return {
        ...state,
        projectFileField: {
          ...state.projectFileField,
          value: action.payload.file,
          errors: undefined
        }
      }
    case ActionType.SET_PROJECT_FILE_ERRORS:
      return {
        ...state,
        projectFileField: {
          ...state.projectFileField,
          errors: action.payload.errors,
          value: undefined
        }
      }
    case ActionType.CLEAR_PROJECT_FILE:
      return {
        ...state,
        projectFileField: { value: undefined, errors: undefined },
        detectedSubComponentIds: [],
        selectedSubComponentIds: []
      }
    case ActionType.UPDATE_MINT_DETAILS:
      return {
        ...state,
        mintDetails: { ...state.mintDetails, ...action.payload.mintDetails }
      }
    case ActionType.SET_DETECTED_SUB_COMPONENT_IDS:
      return {
        ...state,
        detectedSubComponentIds: action.payload.subComponentIds,
        selectedSubComponentIds: state.detectedSubComponentIds.length
          ? state.selectedSubComponentIds
          : action.payload.subComponentIds.filter((x) =>
              state.sounds?.map((sound) => sound.id).includes(x)
            )
      }
    case ActionType.SET_MINTING_STATUS: {
      return {
        ...state,
        mintingStatus: action.payload.status
      }
    }
    case ActionType.MINT_SUCCESS: {
      Segment.track('Song Uploaded', {
        source: window.location.pathname,
        name: action.payload.createdMintStatus.metadata?.title
      })
      return {
        ...state,
        createdMintStatus: action.payload.createdMintStatus,
        mintingStatus: LoadingStatus.SUCCESS,
        page: Page.CONFIRMATION
      }
    }
    case ActionType.ADD_SELECTED_SUB_COMPONENT: {
      return {
        ...state,
        selectedSubComponentIds: [...state.selectedSubComponentIds, action.payload.id]
      }
    }
    case ActionType.REMOVE_SELECTED_SUB_COMPONENT: {
      return {
        ...state,
        selectedSubComponentIds: state.selectedSubComponentIds.filter(
          (x) => x !== action.payload.id
        )
      }
    }
    default:
      return state
  }
}

export const initialState: State = {
  page: Page.INSTRUCTIONS,
  wavFileField: {
    value: undefined,
    errors: undefined
  },
  projectFileField: {
    value: undefined,
    errors: undefined
  },
  mintDetails: {
    title: {},
    key: {},
    genres: { value: [] },
    bpm: { value: 60 },
    color: {},
    customWalletAddress: {}
  },
  detectedSubComponentIds: [],
  selectedSubComponentIds: [],
  mintingStatus: LoadingStatus.IDLE
}

export const useRegisterSongModal = ({
  optionalFields,
  remixOfSamplePack,
  sounds
}: {
  optionalFields: OptionalFields
  remixOfSamplePack?: SamplePack
  sounds?: ArpMedia[]
}) => {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    mintDetails: { ...initialState.mintDetails, color: optionalFields.color ? {} : undefined },
    remixOfSamplePack,
    sounds
  })
  const { uploadedSongCreate } = useMintsAPI()

  const nextPage = () => {
    if (state.page === Page.ATTRIBUTIONS) {
      createSongUploadMint()
      return
    }

    const currentPageIndex = pages.indexOf(state.page)
    let nextPage = pages[currentPageIndex + 1]

    /**
     * If sounds are provided, we direct to the attributions page.
     * If they are not, we are dealing with a split and go to the
     * split details page.
     */
    if (nextPage === Page.DETAILS && sounds !== undefined) {
      nextPage = Page.ATTRIBUTIONS
    }

    if (currentPageIndex < pages.length - 1) {
      dispatch({ type: ActionType.SET_PAGE, payload: { page: nextPage } })
    }
  }

  const previousPage = () => {
    const currentPageIndex = pages.indexOf(state.page)
    const currentPage = pages[currentPageIndex]
    const prevPage =
      currentPage === Page.ATTRIBUTIONS ? Page.UPLOAD_PROJECT : pages[currentPageIndex - 1]

    if (currentPageIndex > 0) {
      dispatch({ type: ActionType.SET_PAGE, payload: { page: prevPage } })
    }
  }

  const createSongUploadMint = async () => {
    dispatch({ type: ActionType.SET_MINTING_STATUS, payload: { status: LoadingStatus.LOADING } })

    try {
      let coverImage: File | undefined
      if (remixOfSamplePack) {
        const result = await axios.get<Blob>(remixOfSamplePack.coverImage.url, {
          params: { info: 'remix' },
          responseType: 'blob'
        })
        coverImage = new File([result.data], remixOfSamplePack.coverImage.name, {
          type: remixOfSamplePack.coverImage.mimeType
        })
      }

      const createdMintStatus = await uploadedSongCreate({
        soundDto: {
          subcomponents: state.sounds
            ? state.selectedSubComponentIds
            : state.detectedSubComponentIds,
          metadata: {
            title: state.mintDetails.title.value,
            genres: state.mintDetails.genres.value,
            key: state.mintDetails.key.value,
            bpm: state.mintDetails.bpm.value
          }
        },
        walletAddress: state.mintDetails.customWalletAddress.value,
        soundFile: state.wavFileField.value,
        coverImage
      })

      dispatch({ type: ActionType.MINT_SUCCESS, payload: { createdMintStatus } })
    } catch (error) {
      console.error(`Error Minting Song: ${JSON.stringify(error)}`)
      dispatch({ type: ActionType.SET_MINTING_STATUS, payload: { status: LoadingStatus.ERROR } })
    }
  }

  const reset = () => {
    dispatch({ type: ActionType.RESET })
  }

  const setWavFile = (file: File) => {
    dispatch({ type: ActionType.SET_WAV_FILE, payload: { file } })
  }

  const clearWavFile = () => {
    dispatch({ type: ActionType.CLEAR_WAV_FILE })
  }

  const setWavFileErrors = (errors: FileError[]) => {
    dispatch({ type: ActionType.SET_WAV_FILE_ERRORS, payload: { errors } })
  }

  const setProjectFile = (file: File) => {
    dispatch({ type: ActionType.SET_PROJECT_FILE, payload: { file } })
  }

  const clearProjectFile = () => {
    dispatch({ type: ActionType.CLEAR_PROJECT_FILE })
  }

  const setProjectFileErrors = (errors: FileError[]) => {
    dispatch({ type: ActionType.SET_PROJECT_FILE_ERRORS, payload: { errors } })
  }

  const setTitle = (value?: string) => {
    const error = resolveStringError('title', value, { required: {}, max: { value: 50 } })
    dispatch({
      type: ActionType.UPDATE_MINT_DETAILS,
      payload: { mintDetails: { title: { value, error } } }
    })
  }

  const setGenres = (value?: string[]) => {
    const error = resolveArrayError('genres', value, {
      required: {},
      max: { message: 'Cannot select more than 3 genres', value: 3 }
    })
    dispatch({
      type: ActionType.UPDATE_MINT_DETAILS,
      payload: { mintDetails: { genres: { value, error } } }
    })
  }

  const setKey = (value?: number) => {
    const error = resolveNumberError('key', value, { required: {} })
    dispatch({
      type: ActionType.UPDATE_MINT_DETAILS,
      payload: { mintDetails: { key: { value, error } } }
    })
  }

  const setBpm = (value?: number) => {
    const error = resolveNumberError('bpm', value, {
      required: {},
      min: { value: 60 },
      max: { value: 320 }
    })
    dispatch({
      type: ActionType.UPDATE_MINT_DETAILS,
      payload: { mintDetails: { bpm: { value, error } } }
    })
  }

  const setColor = (value?: string) => {
    const error = resolveStringError('color', value, { required: {} })
    dispatch({
      type: ActionType.UPDATE_MINT_DETAILS,
      payload: { mintDetails: { color: { value, error } } }
    })
  }

  const setCustomWalletAddress = (value?: string) => {
    dispatch({
      type: ActionType.UPDATE_MINT_DETAILS,
      payload: { mintDetails: { customWalletAddress: { value } } }
    })
  }

  const setDetectedSubComponentIds = (subComponentIds: number[]) => {
    dispatch({ type: ActionType.SET_DETECTED_SUB_COMPONENT_IDS, payload: { subComponentIds } })
  }

  const addSelectedSubComponent = (id: number) => {
    dispatch({ type: ActionType.ADD_SELECTED_SUB_COMPONENT, payload: { id } })
  }

  const removeSelectedSubComponent = (id: number) => {
    dispatch({ type: ActionType.REMOVE_SELECTED_SUB_COMPONENT, payload: { id } })
  }

  const isLastPage = useMemo(() => pages.indexOf(state.page) === pages.length - 1, [state.page])
  const isFirstPage = useMemo(() => pages.indexOf(state.page) === 0, [state.page])

  const isNextPageAllowed = useMemo(() => {
    switch (state.page) {
      case Page.UPLOAD_WAV:
        return state.wavFileField.value !== undefined
      case Page.UPLOAD_PROJECT:
        return state.projectFileField.value !== undefined
      case Page.INSTRUCTIONS:
      case Page.DETAILS:
        return true
      case Page.ATTRIBUTIONS:
        return state.selectedSubComponentIds.length > 0
      case Page.PUBLISH:
        const isColorValid = optionalFields.color
          ? state.mintDetails.color.value && !state.mintDetails.color.error
          : true

        return Boolean(
          state.mintDetails.title.value &&
            !state.mintDetails.title.error &&
            state.mintDetails.bpm.value &&
            !state.mintDetails.bpm.error &&
            isColorValid &&
            state.mintDetails.key.value &&
            !state.mintDetails.key.error &&
            state.mintDetails.genres.value.length &&
            !state.mintDetails.genres.error
        )
      case Page.CONFIRMATION:
        return false
    }
  }, [state])

  return {
    state,
    nextPage,
    previousPage,
    reset,
    setWavFile,
    setWavFileErrors,
    clearWavFile,
    setProjectFile,
    setProjectFileErrors,
    clearProjectFile,
    setTitle,
    setGenres,
    setKey,
    setBpm,
    setColor,
    setDetectedSubComponentIds,
    setCustomWalletAddress,
    addSelectedSubComponent,
    removeSelectedSubComponent,
    isLastPage,
    isFirstPage,
    isNextPageAllowed
  }
}
